"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { useChatAvailability } from "@/hooks/useChatAvailability";
import { Button, Card, TopBar } from "@/components/ui";
import { AuthFlow } from "@/components/AuthFlow";
import { useAuth } from "@/store/auth";
import { useLoyalty } from "@/hooks/useLoyalty";
import { useChallenges, useBadges, useReferral } from "@/hooks/useLoyaltyExpansion";
import { useAddresses } from "@/hooks/useAddresses";
import { useSavedCards } from "@/hooks/useSavedCards";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
// import { Price } from "@/components/Price";

const iconCircle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  display: "grid",
  placeItems: "center",
  color: "var(--ink-1)",
  cursor: "pointer",
};

function initials(name: string | null | undefined): string {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "U";
}

function injectPrice(text: string, amount: number) {
  const parts = text.split("___PRICE___");
  if (parts.length === 1) return text;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
      {parts[0]}
      <Price amount={amount} />
      {parts[1]}
    </span>
  );
}

export function ProfileScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const chatEnabled = useChatAvailability();
  const { format } = useFormatPrice();
  const { isLoggedIn, user, signOut } = useAuth();
  const { loading: loyaltyLoading, error: loyaltyError, balance, rewards, isNotFound: loyaltyNotFound } = useLoyalty();
  const { challengesEnabled } = useChallenges();
  const { badgesEnabled } = useBadges();
  const { enabled: referralEnabled } = useReferral();
  const { addresses } = useAddresses();
  const { cards } = useSavedCards();
  const [signInOpen, setSignInOpen] = useState(false);

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.profile.title")} onBack={() => navigate("/")} />
        <div
          style={{
            padding: "40px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              color: "var(--primary-700)",
            }}
          >
            <Icons.user size={32} />
          </div>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.profile.signin_title")}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              maxWidth: 280,
              lineHeight: 1.45,
            }}
          >
            {t("v2.profile.signin_desc")}
          </div>
          <Button size="lg" onClick={() => setSignInOpen(true)}>
            {t("v2.profile.signin_phone")}
          </Button>
        </div>
        <AuthFlow open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  const displayName = user?.name || user?.phone || t("v2.profile.welcome_back");
  const displayEmail = user?.email || user?.phone || "";
  // The fallback to `user.phone` makes these fields bidi-neutral — render
  // them LTR-isolated so `+201227685199` doesn't get reordered in RTL UIs.
  const nameIsPhone = !user?.name && !!user?.phone;
  const emailIsPhone = !user?.email && !!user?.phone;
  const rows = [
    {
      ic: Icons.pin,
      l: t("v2.profile.saved_addresses"),
      s: t("v2.profile.saved_addresses_sub", { count: addresses.length }),
      a: () => navigate("/addresses"),
    },
    {
      ic: Icons.card,
      l: t("v2.profile.payment_methods"),
      s: t("v2.profile.payment_methods_sub", { count: cards.length }),
      a: () => navigate("/payments"),
    },
    {
      ic: Icons.spark,
      l: t("v2.profile.loyalty_wallet"),
      s: balance
        ? balance.programType === "cashback"
          ? balance.cashback > 0
            ? injectPrice(t("v2.profile.loyalty_cashback_available", { amount: "___PRICE___" }), balance.cashback)
            : t("v2.profile.loyalty_cashback_empty")
          : balance.points > 0
          ? t("v2.profile.loyalty_wallet_sub", { count: balance.points })
          : t("v2.profile.loyalty_wallet_sub_empty")
        : t("v2.profile.loyalty_wallet_sub_empty"),
      a: () => navigate("/loyalty-wallet"),
    },
    {
      ic: Icons.trophy,
      l: t("v2.profile.challenges"),
      s: t("v2.profile.challenges_sub"),
      a: () => navigate("/challenges"),
      hidden: !challengesEnabled,
    },
    {
      ic: Icons.star,
      l: t("v2.profile.badges"),
      s: t("v2.profile.badges_sub"),
      a: () => navigate("/badges"),
      hidden: !badgesEnabled,
    },
    {
      ic: Icons.share,
      l: t("v2.profile.referrals"),
      s: t("v2.profile.referrals_sub"),
      a: () => navigate("/referrals"),
      hidden: !referralEnabled,
    },
    {
      ic: Icons.ticket,
      l: t("v2.offers.title"),
      s: t("v2.offers.profile_row_sub"),
      a: () => navigate("/offers"),
    },
    { ic: Icons.ticket, l: t("v2.profile.promos_gifts"), s: t("v2.profile.promos_gifts_sub"), a: () => {}, hidden: true },
    { ic: Icons.bell, l: t("v2.profile.notifications"), s: t("v2.profile.notifications_sub"), a: () => navigate("/preferences") },
    { ic: Icons.shield, l: t("v2.profile.privacy_security"), s: "", a: () => {}, hidden: true },
    // Web & app chat (R7). `hidden` is driven by the tenant's availability,
    // so the row simply does not exist for a restaurant without the inbox
    // product or with the channel switched off — the customer is never shown
    // a Chat button that leads nowhere, and never learns which plan the
    // restaurant is on.
    {
      ic: Icons.info,
      l: t("v2.chat.title"),
      s: t("v2.chat.profile_row_sub"),
      a: () => navigate("/chat"),
      hidden: !chatEnabled,
    },
  ];
  const loyaltyProgress =
    balance?.nextTier &&
    balance.nextTier.pointsAway > 0 &&
    balance.points + balance.nextTier.pointsAway > 0
      ? Math.max(
          4,
          Math.min(
            100,
            (balance.points / (balance.points + balance.nextTier.pointsAway)) * 100,
          ),
        )
      : 100;

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar title={t("v2.profile.title")} onBack={() => navigate("/")} />
      <div
        style={{
          padding: "8px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <Card padding={16} style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="display"
            style={{
              width: 58,
              height: 58,
              borderRadius: 999,
              background: "var(--primary-100)",
              display: "grid",
              placeItems: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--primary-800)",
            }}
          >
            {initials(user?.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display"
              style={{ fontSize: 16, fontWeight: 700 }}
              dir={nameIsPhone ? "ltr" : undefined}
            >
              {displayName}
            </div>
            <div
              style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 2 }}
              dir={emailIsPhone ? "ltr" : undefined}
            >
              {displayEmail}
            </div>
          </div>
          <button style={iconCircle} onClick={() => navigate("/profile/edit")}>
            <Icons.edit size={16} />
          </button>
        </Card>

        {!loyaltyNotFound && (
          <Card
            padding={16}
          onClick={() => navigate("/loyalty-wallet")}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "var(--primary-50)",
                color: "var(--primary-700)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icons.spark size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 15, fontWeight: 800, color: "var(--ink-1)" }}>
                {t("v2.profile.rewards")}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                {balance
                  ? balance.programType === "cashback"
                    ? balance.cashback > 0
                      ? injectPrice(t("v2.profile.loyalty_cashback_available", {
                          amount: "___PRICE___",
                        }), balance.cashback)
                      : t("v2.profile.loyalty_cashback_empty")
                    : balance.points > 0
                    ? t("v2.profile.loyalty_wallet_sub", { count: balance.points })
                    : t("v2.profile.loyalty_wallet_sub_empty")
                  : t("v2.profile.loyalty_wallet_sub_empty")}
              </div>
            </div>
            <Icons.chevron size={16} style={{ color: "var(--ink-3)" }} />
          </div>

          {loyaltyLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ width: 120, height: 28, borderRadius: 8, background: "var(--surface-2)" }} />
              <div style={{ height: 6, borderRadius: 999, background: "var(--surface-2)" }} />
            </div>
          ) : loyaltyError ? (
            <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
          ) : balance ? (
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span className="display" style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}>
                  {balance.programType === "cashback" ? <Price amount={balance.cashback} /> : balance.points}
                </span>
                <span style={{ fontSize: 13, color: "var(--ink-2)", fontWeight: 700 }}>
                  {balance.programType === "cashback" ? t("v2.loyalty.mode_cashback") : t("v2.profile.points")}
                </span>
                {balance.tier && (
                  <span
                    style={{
                      marginInlineStart: 6,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: "var(--surface-2)",
                      color: "var(--ink-2)",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {balance.tier}
                  </span>
                )}
              </div>
                {balance.nextTier && (
                  <div style={{ marginTop: 12, height: 5, borderRadius: 999, background: "var(--surface-2)", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${Math.max(4, Math.min(100, (balance.points / (balance.points + balance.nextTier.pointsAway)) * 100))}%`,
                        height: "100%",
                        background: "var(--primary-500)",
                      }}
                    />
                  </div>
                )}
                {balance.pointsExpiringSoon > 0 && (
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, color: "#9a3412" }}>
                    <Icons.clock size={14} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>
                      {balance.programType === "cashback"
                        ? injectPrice(t("v2.profile.loyalty_expiring_soon_cashback", { amount: "___PRICE___" }), balance.pointsExpiringSoon)
                        : t("v2.loyalty.expiring_soon", { count: balance.pointsExpiringSoon })}
                    </span>
                  </div>
                )}
              </div>
          ) : null}

          {rewards.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8,
                borderTop: "1px solid var(--border)",
                paddingTop: 12,
              }}
            >
              {rewards.slice(0, 3).map((reward) => (
                <div
                  key={reward.id}
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    background: "var(--surface-2)",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--ink-1)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {reward.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                    {reward.pointsCost} {t("v2.profile.pts")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        )}

        <Card padding={0}>
          {rows.map((r, i) => {
            const Ic = r.ic;
            return (
              <button
                key={r.l}
                onClick={r.a}
                style={{
                  display: r.hidden ? "none" : "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  padding: "14px 16px",
                  background: "transparent",
                  border: 0,
                  textAlign: "start",
                  borderTop: i === 0 ? 0 : "1px solid var(--border)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "var(--surface-2)",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--ink-1)",
                  }}
                >
                  <Ic size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
                    {r.l}
                  </div>
                  {r.s && (
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                      {r.s}
                    </div>
                  )}
                </div>
                <Icons.chevron size={16} style={{ color: "var(--ink-3)" }} />
              </button>
            );
          })}
        </Card>

        <button
          onClick={async () => {
            await signOut();
            navigate("/");
          }}
          style={{
            padding: 14,
            borderRadius: "var(--radius-card)",
            border: "1px solid var(--border)",
            background: "var(--surface-1)",
            color: "var(--ink-2)",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t("v2.profile.sign_out")}
        </button>
      </div>
    </div>
  );
}
